import { message } from 'antd'
import {
  applyMeasurementSystemToFormField,
  assetOverviewPermissionsCheck,
  createUniqueFormFields,
} from 'components/form/formHelpers'
import {
  HandleSaveToRolodexType,
  SimpleFormField,
} from 'components/form/formTypes'
import { GeckoTableForm } from 'components/form/GeckoTableForm'
import { AssetDataObjType } from 'features/assetDatabase/types/assetOverviewTypes'
import { convertUniqueFormFieldsToAssetJSON } from 'features/assetDatabase/utils/assetDBAssetsModels'
import {
  deleteFeatures,
  sortTableByField,
} from 'features/assetDatabase/utils/assetDBHelpers'
import React, { forwardRef, useMemo } from 'react'
import { SubmitHandler } from 'react-hook-form'
import {
  GeckoAssetModel,
  GeckoFeatureModelType,
  GeckoMeasurementSystem,
  regenerateAssetModel,
  SortTableByField,
} from 'types'

interface FeatureFormProps {
  assetDataObj: AssetDataObjType
  componentID: string
  featureType: string
  assetModel: GeckoAssetModel
  sortBy: SortTableByField[]
  measurementSystem: GeckoMeasurementSystem
  inEditMode: boolean
  inResolveMode: boolean
  userPermissions: ReadonlySet<string>
  forceRefresh: () => Promise<void>
  handleFeatureDelete: (featureToRemove: string) => void
  handleFeatureDuplicate: (featureToDuplicate: string) => void
  handleSaveToRolodex: HandleSaveToRolodexType
  deletedFeatures: string[]
}

export const FeatureForm = forwardRef<HTMLFormElement, FeatureFormProps>(
  (
    {
      assetDataObj,
      componentID,
      featureType,
      measurementSystem,
      assetModel,
      sortBy,
      inEditMode,
      inResolveMode,
      userPermissions,
      forceRefresh,
      handleFeatureDelete,
      handleFeatureDuplicate,
      handleSaveToRolodex,
      deletedFeatures,
    },
    ref
  ) => {
    const [messageApi, contextHolder] = message.useMessage()

    const formFieldsByUniqueID = useMemo(() => {
      let returnArr = assetDataObj.components
        .filter(
          (component) => component.componentData.uniqueID === componentID
        )[0]
        .features.filter((feature) => feature.featureJSON.type === featureType)
      if (sortBy && sortBy.length > 0) {
        returnArr = sortTableByField(returnArr, sortBy)
      }
      return returnArr
    }, [assetDataObj.components, componentID, featureType, sortBy])

    const featureFormOnSubmit: SubmitHandler<SimpleFormField> = (data) => {
      const createdFormFields = createUniqueFormFields(formFieldsByUniqueID)
      Object.keys(data).forEach((key) => {
        createdFormFields[key].value = data[key]
      })
      const assetModelRaw = assetModel.raw
      const assetFeatureJSON = convertUniqueFormFieldsToAssetJSON(
        createdFormFields,
        featureType as GeckoFeatureModelType
      )
      const assetModelComponentsJSON = assetModelRaw?.data.components as Record<
        string,
        unknown
      >
      const componentJSON = assetModelComponentsJSON[componentID] as Record<
        string,
        Record<string, unknown>
      >
      componentJSON.features = {
        ...deleteFeatures(componentJSON.features, deletedFeatures),
        ...assetFeatureJSON,
      }
      const editedAssetModel = regenerateAssetModel(
        GeckoAssetModel.CreateFromJSON(assetModelRaw)
      )

      handleSaveToRolodex(
        [editedAssetModel],
        messageApi,
        forceRefresh,
        `${featureType}_${componentID}`
      )
    }

    return (
      <>
        {contextHolder}
        <GeckoTableForm
          formFieldsByUniqueID={formFieldsByUniqueID}
          inEditMode={inEditMode}
          onSubmit={featureFormOnSubmit}
          permissionsCheck={{
            userPermissions: userPermissions,
            permissionsChecker: assetOverviewPermissionsCheck,
          }}
          measurementApplication={{
            measurementSystem: measurementSystem,
            measurementApplication: applyMeasurementSystemToFormField,
          }}
          handleDeleteByID={handleFeatureDelete}
          handleDuplicateByID={handleFeatureDuplicate}
          inResolveMode={inResolveMode}
          ref={ref}
        />
      </>
    )
  }
)

FeatureForm.displayName = 'FeatureForm'
